import React, { useState, useEffect } from 'react';
import {
    Box, Card, CardContent, Grid, Typography, TextField, Button, Stepper, Step, StepLabel,
    Paper, List, ListItem, ListItemText, Chip, Alert, Divider, FormControl,
    InputLabel, Select, MenuItem, IconButton, Accordion, AccordionSummary,
    AccordionDetails, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, FormControlLabel, Checkbox, Stack, Snackbar, useTheme, alpha,
    Tooltip, LinearProgress
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { 
    Psychology as BrainIcon, 
    Add as AddIcon,
    Delete as DeleteIcon,
    DeleteOutline as TrashIcon,
    ExpandMore as ExpandMoreIcon,
    Assignment as AssignmentIcon,
    Timeline as TimelineIcon,
    Engineering as EngineeringIcon,
    Science as ScienceIcon,
    Security as SecurityIcon,
    Analytics as AnalyticsIcon,
    CheckCircle as CheckIcon,
    Visibility as VisibilityIcon,
    Download as DownloadIcon,
    AttachFile as AttachFileIcon,
    CloudUpload as CloudUploadIcon,
    InsertDriveFile as FileIcon
} from '@mui/icons-material';
import { supabase } from '../../../../../../supabaseClient';

export default function D4RootCauseAnalysis({ deviationId, batchLogId, onAnalysisComplete, step, onStepUpdate, stepMapping, stepId, companyId, isReadOnly, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const [activeMethodology, setActiveMethodology] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [analysisData, setAnalysisData] = useState({
        fiveWhys: {
            problem: '',
            whys: [{ question: '', answer: '' }]
        },
        fishbone: {
            problem: '',
            categories: {
                people: [],
                process: [],
                materials: [],
                equipment: [],
                environment: [],
                methods: []
            }
        },
        changeAnalysis: {
            timeframe: '30',
            changes: []
        },
        evidenceCollection: {
            physical: [],
            documentary: [],
            testimonial: [],
            digital: []
        },
        hypotheses: [],
        rootCause: '',
        verification: ''
    });

    // Initialize analysisData from step prop if available (only on mount or when step structure changes)
    useEffect(() => {
        if (step && Object.keys(step).length > 1) { // More than just 'completed' field
            const hasStructuralData = step.fiveWhys || step.fishbone || step.changeAnalysis || 
                                    step.evidenceCollection || step.rootCause || step.verification;
            
            if (hasStructuralData) {
                setAnalysisData({
                    fiveWhys: step.fiveWhys || {
                        problem: '',
                        whys: [{ question: '', answer: '' }]
                    },
                    fishbone: step.fishbone || {
                        problem: '',
                        categories: {
                            people: [],
                            process: [],
                            materials: [],
                            equipment: [],
                            environment: [],
                            methods: []
                        }
                    },
                    changeAnalysis: step.changeAnalysis || {
                        timeframe: '30',
                        changes: []
                    },
                    evidenceCollection: (() => {
                        const stepEvidence = step.evidenceCollection || {
                            physical: [],
                            documentary: [],
                            testimonial: [],
                            digital: []
                        };
                        
                        // Migrate old file structure to new files array structure
                        const migrateEvidence = (evidenceArray) => {
                            return evidenceArray.map(evidence => {
                                // If evidence already has files array, keep it
                                if (evidence.files) {
                                    return evidence;
                                }
                                
                                // If evidence has old file structure, migrate it
                                const migratedEvidence = {
                                    description: evidence.description || '',
                                    location: evidence.location || '',
                                    quality: evidence.quality || 'good',
                                    relevance: evidence.relevance || 'high',
                                    files: []
                                };
                                
                                // Migrate old single file structure to files array
                                if (evidence.fileName || evidence.fileUrl || evidence.filePath) {
                                    migratedEvidence.files.push({
                                        id: Date.now() + Math.random(),
                                        fileName: evidence.fileName || 'Unknown File',
                                        filePath: evidence.filePath || '',
                                        fileUrl: evidence.fileUrl || '',
                                        fileSize: 0, // Unknown for migrated files
                                        fileType: 'application/octet-stream', // Unknown for migrated files
                                        uploadedAt: new Date().toISOString()
                                    });
                                }
                                
                                return migratedEvidence;
                            });
                        };
                        
                        return {
                            physical: migrateEvidence(stepEvidence.physical || []),
                            documentary: migrateEvidence(stepEvidence.documentary || []),
                            testimonial: migrateEvidence(stepEvidence.testimonial || []),
                            digital: migrateEvidence(stepEvidence.digital || [])
                        };
                    })(),
                    hypotheses: step.hypotheses || [],
                    rootCause: step.rootCause || '',
                    verification: step.verification || ''
                });
            }
        }
    }, [step?.fiveWhys, step?.fishbone, step?.changeAnalysis, step?.evidenceCollection, step?.rootCause, step?.verification]);

    // Sync data to parent only when needed (removed automatic sync to prevent infinite loop)
    const syncToParent = (completed = false) => {
        if (onStepUpdate && stepMapping && stepId) {
            onStepUpdate(stepMapping[stepId], {
                ...analysisData,
                completed: completed
            });
        }
    };

    // Auto-sync analysisData changes to parent (with debounce to prevent excessive calls)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (onStepUpdate && stepMapping && stepId && !isReadOnly) {
                onStepUpdate(stepMapping[stepId], {
                    ...analysisData,
                    completed: step?.completed || false
                });
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [analysisData, onStepUpdate, stepMapping, stepId, isReadOnly, step?.completed]);

    const methodologies = [
        { name: '5 Whys Analysis', icon: <BrainIcon />, color: '#ff5722' },
        { name: 'Fishbone Diagram', icon: <TimelineIcon />, color: '#2196f3' },
        { name: 'Change Analysis', icon: <AnalyticsIcon />, color: '#4caf50' },
        { name: 'Evidence Collection', icon: <AssignmentIcon />, color: '#ff9800' },
        { name: 'Root Cause Determination', icon: <CheckIcon />, color: '#9c27b0' }
    ];

    const fishboneCategories = [
        { key: 'people', label: 'People/Personnel', examples: 'Training, experience, fatigue, communication' },
        { key: 'process', label: 'Process/Procedures', examples: 'SOPs, workflow, controls, monitoring' },
        { key: 'materials', label: 'Materials/Ingredients', examples: 'Raw materials, quality, storage, handling' },
        { key: 'equipment', label: 'Equipment/Machinery', examples: 'Calibration, maintenance, wear, design' },
        { key: 'environment', label: 'Environment/Conditions', examples: 'Temperature, humidity, cleanliness, lighting' },
        { key: 'methods', label: 'Methods/Techniques', examples: 'Testing methods, measurement, calculations' }
    ];

    const evidenceTypes = [
        { key: 'physical', label: 'Physical Evidence', examples: 'Samples, photos, damaged parts' },
        { key: 'documentary', label: 'Documentary Evidence', examples: 'Batch records, logs, certificates' },
        { key: 'testimonial', label: 'Testimonial Evidence', examples: 'Interviews, witness statements' },
        { key: 'digital', label: 'Digital Evidence', examples: 'Data logs, alarms, trending data' }
    ];

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const addWhyLevel = () => {
        setAnalysisData(prev => ({
            ...prev,
            fiveWhys: {
                ...prev.fiveWhys,
                whys: [...prev.fiveWhys.whys, { question: '', answer: '' }]
            }
        }));
    };

    const updateWhy = (index, field, value) => {
        setAnalysisData(prev => {
            const newWhys = [...prev.fiveWhys.whys];
            newWhys[index] = { ...newWhys[index], [field]: value };
            return {
                ...prev,
                fiveWhys: { ...prev.fiveWhys, whys: newWhys }
            };
        });
    };

    const addFishboneItem = (category) => {
        setAnalysisData(prev => ({
            ...prev,
            fishbone: {
                ...prev.fishbone,
                categories: {
                    ...prev.fishbone.categories,
                    [category]: [...prev.fishbone.categories[category], '']
                }
            }
        }));
    };

    const updateFishboneItem = (category, index, value) => {
        setAnalysisData(prev => {
            const newCategory = [...prev.fishbone.categories[category]];
            newCategory[index] = value;
            return {
                ...prev,
                fishbone: {
                    ...prev.fishbone,
                    categories: {
                        ...prev.fishbone.categories,
                        [category]: newCategory
                    }
                }
            };
        });
    };

    const addChange = () => {
        setAnalysisData(prev => ({
            ...prev,
            changeAnalysis: {
                ...prev.changeAnalysis,
                changes: [...prev.changeAnalysis.changes, {
                    type: '',
                    description: '',
                    date: '',
                    impact: 'low',
                    relevance: 'unknown'
                }]
            }
        }));
    };

    const updateChange = (index, field, value) => {
        setAnalysisData(prev => {
            const newChanges = [...prev.changeAnalysis.changes];
            newChanges[index] = { ...newChanges[index], [field]: value };
            return {
                ...prev,
                changeAnalysis: { ...prev.changeAnalysis, changes: newChanges }
            };
        });
    };

    const addEvidence = (type) => {
        setAnalysisData(prev => ({
            ...prev,
            evidenceCollection: {
                ...prev.evidenceCollection,
                [type]: [...prev.evidenceCollection[type], {
                    description: '',
                    location: '',
                    quality: 'good',
                    relevance: 'high',
                    files: []
                }]
            }
        }));
    };

    const updateEvidence = (type, index, field, value) => {
        setAnalysisData(prev => {
            const newEvidence = [...prev.evidenceCollection[type]];
            newEvidence[index] = { ...newEvidence[index], [field]: value };
            return {
                ...prev,
                evidenceCollection: {
                    ...prev.evidenceCollection,
                    [type]: newEvidence
                }
            };
        });
    };

    const handleFileUpload = async (event, type, index) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;

        try {
            const uploadPromises = files.map(async (file) => {
                const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${file.name}`;
                const filePath = `${companyId}/d4-evidence/${deviationId}/${type}/${fileName}`;
                
                const { data, error } = await supabase.storage
                    .from('batch-deviations-evidence')
                    .upload(filePath, file, {
                        cacheControl: '3600',
                        upsert: false
                    });

                if (error) {
                    console.error('Upload error details:', error);
                    throw new Error(`Failed to upload ${file.name}: ${error.message}`);
                }

                // Create signed URL for immediate viewing
                const { data: signedUrlData, error: urlError } = await supabase.storage
                    .from('batch-deviations-evidence')
                    .createSignedUrl(filePath, 86400); // 24 hours expiry

                return {
                    id: Date.now() + Math.random(),
                    fileName: file.name,
                    filePath: filePath,
                    fileUrl: urlError ? '' : signedUrlData.signedUrl,
                    fileSize: file.size,
                    fileType: file.type,
                    uploadedAt: new Date().toISOString()
                };
            });

            const uploadedFiles = await Promise.all(uploadPromises);
            
            // Add all uploaded files to the evidence
            setAnalysisData(prev => {
                const newEvidence = [...prev.evidenceCollection[type]];
                const currentFiles = newEvidence[index].files || [];
                newEvidence[index] = { 
                    ...newEvidence[index], 
                    files: [...currentFiles, ...uploadedFiles]
                };
                return {
                    ...prev,
                    evidenceCollection: {
                        ...prev.evidenceCollection,
                        [type]: newEvidence
                    }
                };
            });
            
            showSnackbar(`${uploadedFiles.length} file(s) uploaded successfully!`, 'success');
        } catch (error) {
            console.error('Error uploading files:', error);
            showSnackbar('Error uploading files: ' + error.message, 'error');
        }
    };

    const deleteFile = async (type, evidenceIndex, fileId) => {
        try {
            const evidence = analysisData.evidenceCollection[type][evidenceIndex];
            const fileToDelete = evidence.files.find(f => f.id === fileId);
            
            if (fileToDelete && fileToDelete.filePath) {
                // Delete from Supabase storage
                const { error } = await supabase.storage
                    .from('batch-deviations-evidence')
                    .remove([fileToDelete.filePath]);
                
                if (error) {
                    console.error('Error deleting file from storage:', error);
                    showSnackbar('Warning: File deleted from list but may still exist in storage', 'warning');
                }
            }
            
            // Remove from state
            setAnalysisData(prev => {
                const newEvidence = [...prev.evidenceCollection[type]];
                newEvidence[evidenceIndex] = {
                    ...newEvidence[evidenceIndex],
                    files: newEvidence[evidenceIndex].files.filter(f => f.id !== fileId)
                };
                return {
                    ...prev,
                    evidenceCollection: {
                        ...prev.evidenceCollection,
                        [type]: newEvidence
                    }
                };
            });
            
            showSnackbar('File deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting file:', error);
            showSnackbar('Error deleting file: ' + error.message, 'error');
        }
    };

    const viewFile = async (file) => {
        try {
            if (file.fileUrl) {
                window.open(file.fileUrl, '_blank');
            } else if (file.filePath) {
                // Create a new signed URL if the current one is expired
                const { data: signedUrlData, error } = await supabase.storage
                    .from('batch-deviations-evidence')
                    .createSignedUrl(file.filePath, 86400); // 24 hours expiry

                if (error) {
                    throw new Error('Could not generate file view link: ' + error.message);
                }
                
                window.open(signedUrlData.signedUrl, '_blank');
            }
        } catch (error) {
            console.error('Error viewing file:', error);
            showSnackbar('Error opening file: ' + error.message, 'error');
        }
    };

    const downloadFile = async (file) => {
        try {
            let downloadUrl = file.fileUrl;
            
            if (!downloadUrl && file.filePath) {
                // Create a new signed URL if the current one is expired
                const { data: signedUrlData, error } = await supabase.storage
                    .from('batch-deviations-evidence')
                    .createSignedUrl(file.filePath, 86400);

                if (error) {
                    throw new Error('Could not generate download link: ' + error.message);
                }
                
                downloadUrl = signedUrlData.signedUrl;
            }
            
            if (downloadUrl) {
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = file.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            showSnackbar('Error downloading file: ' + error.message, 'error');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };



    // Validation function to check if all required fields are filled
    const isFormValid = () => {
        // Check if we have meaningful 5 Whys analysis
        const hasFiveWhys = analysisData.fiveWhys.problem?.trim() &&
                           analysisData.fiveWhys.whys.some(why => 
                               why.question?.trim() && why.answer?.trim()
                           );

        // Check if we have meaningful Fishbone analysis
        const hasFishbone = analysisData.fishbone.problem?.trim() &&
                           Object.values(analysisData.fishbone.categories).some(category => 
                               category.some(item => item?.trim())
                           );

        // Check if we have some change analysis
        const hasChangeAnalysis = analysisData.changeAnalysis.changes.length > 0 &&
                                 analysisData.changeAnalysis.changes.some(change => 
                                     change.description?.trim() && change.type
                                 );

        // Check if we have some evidence collection
        const hasEvidence = Object.values(analysisData.evidenceCollection).some(evidenceType => 
                               evidenceType.length > 0 && 
                               evidenceType.some(evidence => evidence.description?.trim())
                           );

        // Check if we have root cause and verification
        const hasRootCause = analysisData.rootCause?.trim() && 
                            analysisData.verification?.trim();

        return hasFiveWhys && hasFishbone && hasChangeAnalysis && 
               hasEvidence && hasRootCause;
    };

    const render5Whys = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#ff5722', 0.1)}, ${alpha('#ff5722', 0.05)})`,
                        border: `1px solid ${alpha('#ff5722', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <BrainIcon sx={{ fontSize: 24, color: '#ff5722' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            5 Whys Root Cause Analysis
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Sequential questioning methodology
                        </Typography>
                    </Box>
                </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Start with the problem statement, then ask "Why?" for each answer until you reach the root cause.
                Typically takes 3-7 levels to find the true root cause.
            </Alert>
            
            <TextField
                fullWidth
                label="Problem Statement"
                placeholder="What specific problem occurred?"
                value={analysisData.fiveWhys.problem}
                onChange={isReadOnly ? undefined : (e) => setAnalysisData(prev => ({
                    ...prev,
                    fiveWhys: { ...prev.fiveWhys, problem: e.target.value }
                }))}
                InputProps={{ readOnly: isReadOnly }}
                sx={{ 
                    mb: 4,
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                    }
                }}
                multiline
                rows={4}
                variant="outlined"
            />
            
            {analysisData.fiveWhys.whys.map((why, index) => (
                <Paper key={index} sx={{ 
                    p: 3, 
                    mb: 2, 
                    backgroundColor: '#f8f9fa',
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.15)}`,
                        border: `1px solid ${theme.palette.grey[600]}`
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#ff5722' }}>
                        Why Level {index + 1}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <TextField
                                fullWidth
                                label={`Why ${index + 1}?`}
                                placeholder="Ask why this happened..."
                                value={why.question}
                                onChange={isReadOnly ? undefined : (e) => updateWhy(index, 'question', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                multiline
                                rows={3}
                                variant="outlined"
                                sx={{ 
                                    mb: 1,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Answer/Cause"
                                placeholder="Because..."
                                value={why.answer}
                                onChange={isReadOnly ? undefined : (e) => updateWhy(index, 'answer', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                multiline
                                rows={3}
                                variant="outlined"
                                sx={{ 
                                    mb: 1,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={1}>
                            {index > 0 && (
                                <IconButton 
                                    color="error"
                                    onClick={isReadOnly ? undefined : () => {
                                        const newWhys = analysisData.fiveWhys.whys.filter((_, i) => i !== index);
                                        setAnalysisData(prev => ({
                                            ...prev,
                                            fiveWhys: { ...prev.fiveWhys, whys: newWhys }
                                        }));
                                        showSnackbar('Why level deleted successfully', 'success');
                                    }}
                                    disabled={isReadOnly}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Grid>
                    </Grid>
                    
                    {why.answer && index === analysisData.fiveWhys.whys.length - 1 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Can you ask "Why?" again for this answer? If yes, add another level.
                            If no, you may have found your root cause.
                        </Alert>
                    )}
                </Paper>
            ))}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={isReadOnly ? undefined : addWhyLevel}
                    disabled={isReadOnly}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        opacity: isReadOnly ? 0.6 : 1,
                        '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                    }}
                >
                    Add Another Why Level
                </Button>
            </Box>
            </CardContent>
        </Card>
    );

    const renderFishbone = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#2196f3', 0.1)}, ${alpha('#2196f3', 0.05)})`,
                        border: `1px solid ${alpha('#2196f3', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <TimelineIcon sx={{ fontSize: 24, color: '#2196f3' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Fishbone Diagram Analysis
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Systematic cause categorization
                        </Typography>
                    </Box>
                </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Systematically examine potential causes in each category. Think about what could go wrong in each area.
            </Alert>
            
            <TextField
                fullWidth
                label="Problem Statement"
                placeholder="What is the effect/problem you're analyzing?"
                value={analysisData.fishbone.problem}
                onChange={isReadOnly ? undefined : (e) => setAnalysisData(prev => ({
                    ...prev,
                    fishbone: { ...prev.fishbone, problem: e.target.value }
                }))}
                InputProps={{ readOnly: isReadOnly }}
                sx={{ 
                    mb: 4,
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                    }
                }}
                multiline
                rows={4}
                variant="outlined"
            />
            
            <Grid container spacing={3}>
                {fishboneCategories.map((category) => (
                    <Grid item xs={12} md={6} key={category.key}>
                                                    <Card sx={{ 
                                                        borderRadius: 4, 
                                                        border: `1px solid ${theme.palette.grey[400]}`,
                                                        background: 'white',
                                                        backdropFilter: 'blur(20px)',
                                                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                                        },
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                                    }}>
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                                <Box sx={{
                                                                    p: 1, 
                                                                    borderRadius: 2, 
                                                                    background: `linear-gradient(${alpha('#2196f3', 0.1)}, ${alpha('#2196f3', 0.05)})`,
                                                                    border: `1px solid ${alpha('#2196f3', 0.2)}`,
                                                                    backdropFilter: 'blur(10px)',
                                                                    mr: 1.5
                                                                }}>
                                                                    <TimelineIcon sx={{ fontSize: 20, color: '#2196f3' }} />
                                                                </Box>
                                                                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                                                    {category.label}
                                                                </Typography>
                                                            </Box>
                            <Typography variant="caption" sx={{ display: 'block', mb: 2, color: theme.palette.text.secondary }}>
                                {category.examples}
                            </Typography>
                            
                            {analysisData.fishbone.categories[category.key].map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TextField
                                        fullWidth
                                        placeholder="Potential cause..."
                                        value={item}
                                        onChange={isReadOnly ? undefined : (e) => updateFishboneItem(category.key, index, e.target.value)}
                                        InputProps={{ readOnly: isReadOnly }}
                                        variant="outlined"
                                        sx={{ 
                                            mb: 1,
                                            '& .MuiOutlinedInput-root': { 
                                                borderRadius: 2,
                                                '& fieldset': {
                                                                                                    borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                },
                                                '&:hover fieldset': {
                                                                                                    borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                },
                                                '&.Mui-focused fieldset': {
                                                                                                    borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                }
                                            }
                                        }}
                                    />
                                    <IconButton 
                                        size="small" 
                                        color="error"
                                        onClick={isReadOnly ? undefined : () => {
                                            const newItems = analysisData.fishbone.categories[category.key].filter((_, i) => i !== index);
                                            setAnalysisData(prev => ({
                                                ...prev,
                                                fishbone: {
                                                    ...prev.fishbone,
                                                    categories: {
                                                        ...prev.fishbone.categories,
                                                        [category.key]: newItems
                                                    }
                                                }
                                            }));
                                            showSnackbar('Fishbone cause deleted successfully', 'success');
                                        }}
                                        disabled={isReadOnly}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            ))}
                            
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={isReadOnly ? undefined : () => addFishboneItem(category.key)}
                                disabled={isReadOnly}
                                sx={{ mt: 1, opacity: isReadOnly ? 0.6 : 1 }}
                            >
                                Add Cause
                            </Button>
                                                        </CardContent>
                                                    </Card>
                    </Grid>
                ))}
            </Grid>
            </CardContent>
        </Card>
    );

    const renderChangeAnalysis = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#4caf50', 0.1)}, ${alpha('#4caf50', 0.05)})`,
                        border: `1px solid ${alpha('#4caf50', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <AnalyticsIcon sx={{ fontSize: 24, color: '#4caf50' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Change Analysis
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Identify changes before the problem
                        </Typography>
                    </Box>
                </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Many problems are caused by changes. Identify what changed before the problem occurred.
            </Alert>
            
            <FormControl sx={{ mb: 3, minWidth: 200 }}>
                <InputLabel>Analysis Timeframe</InputLabel>
                <Select
                    value={analysisData.changeAnalysis.timeframe}
                    onChange={isReadOnly ? undefined : (e) => setAnalysisData(prev => ({
                        ...prev,
                        changeAnalysis: { ...prev.changeAnalysis, timeframe: e.target.value }
                    }))}
                    inputProps={{ readOnly: isReadOnly }}
                    label="Analysis Timeframe"
                    sx={{ 
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.grey[400],
                            borderWidth: 1
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.grey[600],
                            borderWidth: 1
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.grey[700],
                            borderWidth: 1
                        }
                    }}
                >
                    <MenuItem value="7">Last 7 days</MenuItem>
                    <MenuItem value="30">Last 30 days</MenuItem>
                    <MenuItem value="90">Last 90 days</MenuItem>
                    <MenuItem value="365">Last year</MenuItem>
                </Select>
            </FormControl>
            
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Changes in the last {analysisData.changeAnalysis.timeframe} days:
            </Typography>
            
            {analysisData.changeAnalysis.changes.map((change, index) => (
                <Paper key={index} sx={{ 
                    p: 4, 
                    mb: 4, 
                    backgroundColor: alpha(theme.palette.success.main, 0.05), 
                    borderRadius: 3, 
                    border: `1px solid ${theme.palette.success.main}`, 
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.1)}`
                }}>
                    <Grid container spacing={4}>
                        {/* Header Row - Change Type and Date */}
                        <Grid item xs={12} md={8}>
                            <FormControl fullWidth>
                                <InputLabel>Change Type</InputLabel>
                                <Select
                                    value={change.type}
                                    onChange={isReadOnly ? undefined : (e) => updateChange(index, 'type', e.target.value)}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Change Type"
                                    sx={{ 
                                        mb: 2,
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="personnel">Personnel</MenuItem>
                                    <MenuItem value="procedure">Procedure/SOP</MenuItem>
                                    <MenuItem value="material">Material/Supplier</MenuItem>
                                    <MenuItem value="equipment">Equipment/Maintenance</MenuItem>
                                    <MenuItem value="environment">Environment</MenuItem>
                                    <MenuItem value="training">Training</MenuItem>
                                    <MenuItem value="software">Software/System</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                                                                        <DatePicker
                                                label="Date"
                                                value={change.date ? dayjs(change.date) : null}
                                                onChange={isReadOnly ? undefined : (newValue) => updateChange(index, 'date', newValue ? newValue.format('YYYY-MM-DD') : '')}
                                                disabled={isReadOnly}
                                                format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY' : 'MM/DD/YYYY'}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        sx: { 
                                                            mb: 2,
                                                            '& .MuiOutlinedInput-root': { 
                                                                borderRadius: 2,
                                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                        </Grid>
                        
                        {/* Description - Full Width */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description of Change"
                                value={change.description}
                                onChange={isReadOnly ? undefined : (e) => updateChange(index, 'description', e.target.value)}
                                InputProps={{ readOnly: isReadOnly }}
                                placeholder="What specifically changed? Provide detailed information about the nature, scope, and timing of this change..."
                                multiline
                                rows={5}
                                variant="outlined"
                                sx={{ 
                                    mb: 3,
                                    '& .MuiOutlinedInput-root': { 
                                        borderRadius: 2,
                                        fontSize: '1.1rem',
                                        lineHeight: 1.6,
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>
                        
                        {/* Assessment Row - Impact and Relevance */}
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Potential Impact</InputLabel>
                                <Select
                                    value={change.impact}
                                    onChange={isReadOnly ? undefined : (e) => updateChange(index, 'impact', e.target.value)}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Potential Impact"
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="low">Low - Minimal effect on process/outcome</MenuItem>
                                    <MenuItem value="medium">Medium - Moderate effect on process/outcome</MenuItem>
                                    <MenuItem value="high">High - Significant effect on process/outcome</MenuItem>
                                    <MenuItem value="critical">Critical - Major effect on process/outcome</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Relevance to Problem</InputLabel>
                                <Select
                                    value={change.relevance}
                                    onChange={isReadOnly ? undefined : (e) => updateChange(index, 'relevance', e.target.value)}
                                    inputProps={{ readOnly: isReadOnly }}
                                    label="Relevance to Problem"
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                            borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                        }
                                    }}
                                >
                                    <MenuItem value="unknown">Unknown - Need more analysis</MenuItem>
                                    <MenuItem value="unlikely">Unlikely - Probably not related</MenuItem>
                                    <MenuItem value="possible">Possible - Could be related</MenuItem>
                                    <MenuItem value="likely">Likely - Probably related</MenuItem>
                                    <MenuItem value="certain">Certain - Definitely related</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        
                        {/* Delete Button Row */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<TrashIcon />}
                                    onClick={isReadOnly ? undefined : () => {
                                        const newChanges = analysisData.changeAnalysis.changes.filter((_, i) => i !== index);
                                        setAnalysisData(prev => ({
                                            ...prev,
                                            changeAnalysis: { ...prev.changeAnalysis, changes: newChanges }
                                        }));
                                        showSnackbar('Change record deleted successfully', 'success');
                                    }}
                                    disabled={isReadOnly}
                                    sx={{
                                        borderRadius: 2,
                                        px: 3,
                                        py: 1,
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                        opacity: isReadOnly ? 0.6 : 1,
                                        '&:hover': {
                                            backgroundColor: '#ffebee'
                                        }
                                    }}
                                >
                                    Delete Change
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={isReadOnly ? undefined : addChange}
                    disabled={isReadOnly}
                    sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        opacity: isReadOnly ? 0.6 : 1,
                        '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: alpha(theme.palette.primary.main, 0.05)
                        }
                    }}
                >
                    Add Change
                </Button>
            </Box>
            </CardContent>
        </Card>
    );

    const renderEvidenceCollection = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#ff9800', 0.1)}, ${alpha('#ff9800', 0.05)})`,
                        border: `1px solid ${alpha('#ff9800', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <AssignmentIcon sx={{ fontSize: 24, color: '#ff9800' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Evidence Collection
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Gather supporting documentation
                        </Typography>
                    </Box>
                </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Collect and document all evidence that supports your investigation. Quality evidence is crucial for verification.
            </Alert>
            
            {evidenceTypes.map((type) => (
                <Accordion key={type.key} sx={{ 
                    mb: 2,
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                    overflow: 'hidden',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${theme.palette.grey[600]}`
                    },
                    '&:before': {
                        display: 'none'
                    },
                    '&.Mui-expanded': {
                        borderRadius: '12px !important'
                    },
                    '& .MuiAccordion-root': {
                        borderRadius: '12px !important'
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <AccordionSummary 
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                            borderRadius: '12px 12px 0 0',
                            '& .MuiAccordionSummary-content': {
                                my: 1
                            },
                            '&.Mui-expanded': {
                                borderRadius: '12px 12px 0 0'
                            }
                        }}
                    >
                        <Typography variant="h6">
                            {type.label} ({analysisData.evidenceCollection[type.key].length})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{
                        borderRadius: '0 0 12px 12px',
                        '&:last-child': {
                            borderRadius: '0 0 12px 12px'
                        }
                    }}>
                        <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
                            {type.examples}
                        </Typography>
                        
                                                {analysisData.evidenceCollection[type.key].map((evidence, index) => (
                            <Paper key={index} sx={{ 
                                p: 4, 
                                mb: 4, 
                                backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.grey[500], 0.1)}`,
                                position: 'relative'
                            }}>
                                {/* Delete Button - Top Right Corner */}
                                <IconButton 
                                    color="error"
                                    size="medium"
                                    onClick={isReadOnly ? undefined : () => {
                                        const newEvidence = analysisData.evidenceCollection[type.key].filter((_, i) => i !== index);
                                        setAnalysisData(prev => ({
                                            ...prev,
                                            evidenceCollection: {
                                                ...prev.evidenceCollection,
                                                [type.key]: newEvidence
                                            }
                                        }));
                                        showSnackbar('Evidence deleted successfully', 'success');
                                    }}
                                    disabled={isReadOnly}
                                    sx={{ 
                                        position: 'absolute',
                                        top: 16,
                                        right: 16,
                                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                                        opacity: isReadOnly ? 0.3 : 1,
                                        '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.2) },
                                        zIndex: 1
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>

                                <Grid container spacing={4}>
                                    {/* Evidence Description - Full Width with more height */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 500, color: theme.palette.text.primary, pr: 6 }}>
                                            Evidence Details
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            label="Evidence Description"
                                            value={evidence.description}
                                            onChange={isReadOnly ? undefined : (e) => updateEvidence(type.key, index, 'description', e.target.value)}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="Provide a detailed description of this evidence..."
                                            multiline
                                            rows={6}
                                            variant="outlined"
                                            sx={{ 
                                                mb: 3,
                                                '& .MuiOutlinedInput-root': { 
                                                    borderRadius: 2,
                                                    fontSize: '1.1rem',
                                                    lineHeight: 1.6,
                                                    '& fieldset': {
                                                                                                        borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                    },
                                                    '&:hover fieldset': {
                                                                                                        borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                                                                        borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    
                                    {/* Location and File Upload Row */}
                                    <Grid item xs={12} md={8}>
                                        <TextField
                                            fullWidth
                                            label="Location/Reference"
                                            value={evidence.location}
                                            onChange={isReadOnly ? undefined : (e) => updateEvidence(type.key, index, 'location', e.target.value)}
                                            InputProps={{ readOnly: isReadOnly }}
                                            placeholder="File path, physical location, document reference number..."
                                            multiline
                                            rows={4}
                                            variant="outlined"
                                            sx={{
                                                '& .MuiOutlinedInput-root': { 
                                                    borderRadius: 2,
                                                    fontSize: '1rem',
                                                    lineHeight: 1.5,
                                                    '& fieldset': {
                                                                                                        borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                    },
                                                    '&:hover fieldset': {
                                                                                                        borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                                                                        borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            {/* File Upload Section */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <input
                                                    type="file"
                                                    id={`file-upload-${type.key}-${index}`}
                                                    style={{ display: 'none' }}
                                                    onChange={isReadOnly ? undefined : (e) => handleFileUpload(e, type.key, index)}
                                                    disabled={isReadOnly}
                                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.jpg,.jpeg,.png,.gif,.bmp,.tiff,.webp,.mp4,.avi,.mov,.wmv,.flv,.mkv,.zip,.rar,.7z"
                                                    multiple
                                                />
                                                <label htmlFor={`file-upload-${type.key}-${index}`}>
                                                    <Button
                                                        component="span"
                                                        variant="contained"
                                                        startIcon={<CloudUploadIcon />}
                                                        disabled={isReadOnly}
                                                        sx={{ 
                                                            py: 1.5,
                                                            px: 3,
                                                            backgroundColor: theme.palette.primary.main,
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            fontSize: '0.95rem',
                                                            borderRadius: 2,
                                                            opacity: isReadOnly ? 0.6 : 1,
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.primary.dark
                                                            }
                                                        }}
                                                    >
                                                        Upload Files
                                                    </Button>
                                                </label>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    Select multiple files to upload
                                                </Typography>
                                            </Box>

                                            {/* Files List */}
                                            {evidence.files && evidence.files.length > 0 && (
                                                <Box sx={{ 
                                                    border: `1px solid ${theme.palette.grey[400]}`,
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    backgroundColor: alpha(theme.palette.grey[50], 0.3)
                                                }}>
                                                    <Box sx={{ 
                                                        p: 2, 
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                        borderBottom: `1px solid ${theme.palette.grey[400]}`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1
                                                    }}>
                                                        <AttachFileIcon sx={{ color: theme.palette.primary.main }} />
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                                            Evidence Files ({evidence.files.length})
                                                        </Typography>
                                                    </Box>
                                                    <List sx={{ p: 0 }}>
                                                        {evidence.files.map((file, fileIndex) => (
                                                            <ListItem 
                                                                key={file.id}
                                                                sx={{ 
                                                                    borderBottom: fileIndex < evidence.files.length - 1 ? `1px solid ${theme.palette.grey[300]}` : 'none',
                                                                    py: 2,
                                                                    px: 3,
                                                                    '&:hover': {
                                                                        backgroundColor: alpha(theme.palette.grey[100], 0.5)
                                                                    }
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                                                                    <FileIcon sx={{ 
                                                                        fontSize: 24, 
                                                                        color: 'text.secondary',
                                                                        minWidth: 24 
                                                                    }} />
                                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                        <Typography 
                                                                            variant="body2" 
                                                                            sx={{ 
                                                                                fontWeight: 500, 
                                                                                color: theme.palette.text.primary,
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                        >
                                                                            {file.fileName}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {formatFileSize(file.fileSize)} • {new Date(file.uploadedAt).toLocaleDateString()}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <Tooltip title="View File">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => viewFile(file)}
                                                                                sx={{ 
                                                                                    color: theme.palette.info.main,
                                                                                    '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.1) }
                                                                                }}
                                                                            >
                                                                                <VisibilityIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Download File">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => downloadFile(file)}
                                                                                sx={{ 
                                                                                    color: theme.palette.success.main,
                                                                                    '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) }
                                                                                }}
                                                                            >
                                                                                <DownloadIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Delete File">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={isReadOnly ? undefined : () => deleteFile(type.key, index, file.id)}
                                                                                disabled={isReadOnly}
                                                                                sx={{ 
                                                                                    color: theme.palette.error.main,
                                                                                    opacity: isReadOnly ? 0.3 : 1,
                                                                                    '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                                                                                }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                </Box>
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}

                                            {(!evidence.files || evidence.files.length === 0) && (
                                                <Box sx={{ 
                                                    textAlign: 'center', 
                                                    p: 4, 
                                                    backgroundColor: alpha(theme.palette.grey[100], 0.3),
                                                    borderRadius: 3,
                                                    border: `2px dashed ${theme.palette.grey[400]}`
                                                }}>
                                                    <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.grey[500], mb: 1 }} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        No files uploaded yet. Click "Upload Files" to add evidence.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                    
                                    {/* Quality and Relevance Row */}
                                    <Grid item xs={12}>
                                        <Typography variant="h6" sx={{ mb: 3, fontWeight: 500, color: theme.palette.text.primary }}>
                                            Evidence Assessment
                                        </Typography>
                                        <Grid container spacing={4}>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="large">
                                                    <InputLabel>Evidence Quality</InputLabel>
                                                    <Select
                                                        value={evidence.quality}
                                                        onChange={isReadOnly ? undefined : (e) => updateEvidence(type.key, index, 'quality', e.target.value)}
                                                        inputProps={{ readOnly: isReadOnly }}
                                                        label="Evidence Quality"
                                                        variant="outlined"
                                                        sx={{ 
                                                            fontSize: '1rem',
                                                            borderRadius: 2,
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                                                borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                                                                borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                                                borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="poor">Poor - Incomplete/Damaged</MenuItem>
                                                        <MenuItem value="fair">Fair - Some Limitations</MenuItem>
                                                        <MenuItem value="good">Good - Reliable</MenuItem>
                                                        <MenuItem value="excellent">Excellent - Comprehensive</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth size="large">
                                                    <InputLabel>Relevance to Investigation</InputLabel>
                                                    <Select
                                                        value={evidence.relevance}
                                                        onChange={isReadOnly ? undefined : (e) => updateEvidence(type.key, index, 'relevance', e.target.value)}
                                                        inputProps={{ readOnly: isReadOnly }}
                                                        label="Relevance to Investigation"
                                                        variant="outlined"
                                                        sx={{ 
                                                            fontSize: '1rem',
                                                            borderRadius: 2,
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                                                borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                                                                borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                                                                borderColor: theme.palette.grey[400],
                                                borderWidth: 1
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="low">Low - Minor relevance</MenuItem>
                                                        <MenuItem value="medium">Medium - Moderate relevance</MenuItem>
                                                        <MenuItem value="high">High - Important evidence</MenuItem>
                                                        <MenuItem value="critical">Critical - Key evidence</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Paper>
                        ))}
                        
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={isReadOnly ? undefined : () => addEvidence(type.key)}
                            disabled={isReadOnly}
                            sx={{ opacity: isReadOnly ? 0.6 : 1 }}
                        >
                            Add {type.label}
                        </Button>
                    </AccordionDetails>
                </Accordion>
            ))}
            </CardContent>
        </Card>
    );

    const renderRootCauseDetermination = () => (
        <Card sx={{ 
            borderRadius: 4, 
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
            <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha('#9c27b0', 0.1)}, ${alpha('#9c27b0', 0.05)})`,
                        border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <CheckIcon sx={{ fontSize: 24, color: '#9c27b0' }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            Root Cause Determination
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Final analysis and verification
                        </Typography>
                    </Box>
                </Box>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
                Based on your analysis above, determine the true root cause. This should be the fundamental reason 
                that, if addressed, will prevent recurrence.
            </Alert>
            
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Analysis Summary:
            </Typography>
            
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${alpha('#ff5722', 0.3)}`,
                        background: alpha('#ff5722', 0.05),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 4px 16px ${alpha('#ff5722', 0.1)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#ff5722', 0.15)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{
                                    p: 1, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha('#ff5722', 0.1)}, ${alpha('#ff5722', 0.05)})`,
                                    border: `1px solid ${alpha('#ff5722', 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 1.5
                                }}>
                                    <BrainIcon sx={{ fontSize: 20, color: '#ff5722' }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#ff5722' }}>
                                    5 Whys Conclusion:
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {analysisData.fiveWhys.whys.length > 0 && analysisData.fiveWhys.whys[analysisData.fiveWhys.whys.length - 1].answer || 'Not completed'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${alpha('#2196f3', 0.3)}`,
                        background: alpha('#2196f3', 0.05),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 4px 16px ${alpha('#2196f3', 0.1)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#2196f3', 0.15)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{
                                    p: 1, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha('#2196f3', 0.1)}, ${alpha('#2196f3', 0.05)})`,
                                    border: `1px solid ${alpha('#2196f3', 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 1.5
                                }}>
                                    <AnalyticsIcon sx={{ fontSize: 20, color: '#2196f3' }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#2196f3' }}>
                                    High-Impact Changes:
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {analysisData.changeAnalysis.changes.filter(c => c.impact === 'high' || c.impact === 'critical').length} identified
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${alpha('#4caf50', 0.3)}`,
                        background: alpha('#4caf50', 0.05),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 4px 16px ${alpha('#4caf50', 0.1)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#4caf50', 0.15)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{
                                    p: 1, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha('#4caf50', 0.1)}, ${alpha('#4caf50', 0.05)})`,
                                    border: `1px solid ${alpha('#4caf50', 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 1.5
                                }}>
                                    <AssignmentIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#4caf50' }}>
                                    Critical Evidence:
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {Object.values(analysisData.evidenceCollection).flat().filter(e => e.relevance === 'critical').length} pieces collected
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${alpha('#9c27b0', 0.3)}`,
                        background: alpha('#9c27b0', 0.05),
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 4px 16px ${alpha('#9c27b0', 0.1)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#9c27b0', 0.15)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Box sx={{
                                    p: 1, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha('#9c27b0', 0.1)}, ${alpha('#9c27b0', 0.05)})`,
                                    border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 1.5
                                }}>
                                    <TimelineIcon sx={{ fontSize: 20, color: '#9c27b0' }} />
                                </Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#9c27b0' }}>
                                    Fishbone Categories:
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {Object.values(analysisData.fishbone.categories).flat().filter(c => c.trim()).length} potential causes identified
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            
            <TextField
                fullWidth
                multiline
                rows={8}
                label="Root Cause Statement"
                placeholder="Based on your analysis, what is the fundamental root cause of this problem? Be specific and focus on the underlying system or process failure."
                value={analysisData.rootCause}
                onChange={isReadOnly ? undefined : (e) => setAnalysisData(prev => ({ ...prev, rootCause: e.target.value }))}
                InputProps={{ readOnly: isReadOnly }}
                sx={{ 
                    mb: 4,
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                    }
                }}
                variant="outlined"
            />
            
            <TextField
                fullWidth
                multiline
                rows={6}
                label="Verification Plan"
                placeholder="How will you verify this root cause is correct? What tests, trials, or analyses will confirm your findings?"
                value={analysisData.verification}
                onChange={isReadOnly ? undefined : (e) => setAnalysisData(prev => ({ ...prev, verification: e.target.value }))}
                InputProps={{ readOnly: isReadOnly }}
                sx={{ 
                    mb: 4,
                    '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                    }
                }}
                variant="outlined"
            />
            </CardContent>
        </Card>
    );

    const renderMethodology = () => {
        switch (activeMethodology) {
            case 0: return render5Whys();
            case 1: return renderFishbone();
            case 2: return renderChangeAnalysis();
            case 3: return renderEvidenceCollection();
            case 4: return renderRootCauseDetermination();
            default: return null;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                p: 4, 
                backgroundColor: alpha(theme.palette.warning.main, 0.1), 
                borderRadius: 4, 
                border: `1px solid ${theme.palette.warning.main}`, 
                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.12)}`,
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{
                        p: 1.5, 
                        borderRadius: 2, 
                        background: `linear-gradient(${alpha(theme.palette.warning.main, 0.1)}, ${alpha(theme.palette.warning.main, 0.05)})`,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        mr: 2
                    }}>
                        <BrainIcon sx={{ fontSize: 24, color: theme.palette.warning.main }} />
                    </Box>
                    <Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 500, 
                            color: theme.palette.warning.dark, 
                            mb: 0.5
                        }}>
                            D4: Comprehensive Root Cause Analysis
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Use proven methodologies to systematically identify the true root cause
                        </Typography>
                    </Box>
                </Box>

                    <Stepper activeStep={activeMethodology} sx={{ mb: 4 }}>
                        {methodologies.map((method, index) => (
                            <Step key={method.name} completed={
                                index === 0 ? analysisData.fiveWhys.whys.some(w => w.answer) :
                                index === 1 ? Object.values(analysisData.fishbone.categories).flat().some(c => c.trim()) :
                                index === 2 ? analysisData.changeAnalysis.changes.length > 0 :
                                index === 3 ? Object.values(analysisData.evidenceCollection).flat().length > 0 :
                                analysisData.rootCause && analysisData.verification
                            }>
                                <StepLabel 
                                    onClick={() => {
                                        if (!isReadOnly) syncToParent(step?.completed || false);
                                        setActiveMethodology(index);
                                    }}
                                    sx={{ cursor: 'pointer' }}
                                >
                                    {method.name}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {renderMethodology()}

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button 
                            variant="outlined"
                            disabled={activeMethodology === 0}
                            onClick={() => {
                                if (!isReadOnly) syncToParent(step?.completed || false);
                                setActiveMethodology(prev => prev - 1);
                            }}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                py: 1.5,
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                fontWeight: 500,
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                },
                                '&.Mui-disabled': {
                                    borderColor: theme.palette.grey[300],
                                    color: theme.palette.grey[400]
                                }
                            }}
                        >
                            ← Previous Method
                        </Button>
                        <Button 
                            variant="contained"
                            disabled={activeMethodology === methodologies.length - 1}
                            onClick={() => {
                                if (!isReadOnly) syncToParent(step?.completed || false);
                                setActiveMethodology(prev => prev + 1);
                            }}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                color: 'white',
                                fontWeight: 500,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                '&:hover': {
                                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                },
                                '&.Mui-disabled': {
                                    background: theme.palette.grey[300],
                                    color: theme.palette.grey[500],
                                    boxShadow: 'none'
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            Next Method →
                        </Button>
                    </Box>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        p: 3, 
                        backgroundColor: 'white',
                        borderRadius: 4, 
                        border: `1px solid ${isFormValid() ? theme.palette.success.main : theme.palette.grey[400]}`,
                        boxShadow: `0 8px 32px ${alpha(isFormValid() ? theme.palette.success.main : theme.palette.grey[500], 0.12)}`,
                        backdropFilter: 'blur(20px)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        mt: 4
                    }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={step?.completed || false}
                                    onChange={isReadOnly ? undefined : async (e) => {
                                        if (!isFormValid() && e.target.checked) {
                                            showSnackbar('Please complete all analysis methodologies with meaningful data before marking as complete.', 'warning');
                                            return;
                                        }
                                        
                                        if (e.target.checked) {
                                            if (!analysisData.rootCause || !analysisData.verification) {
                                                showSnackbar('Please complete root cause and verification fields first', 'warning');
                                                return;
                                            }
                                            
                                            try {
                                                await supabase
                                                    .from('batch_log_deviations')
                                                    .update({
                                                        root_cause_analysis: analysisData,
                                                        root_cause: analysisData.rootCause,
                                                        status: 'capa_required',
                                                        updated_at: new Date().toISOString()
                                                    })
                                                    .eq('id', deviationId);
                                                
                                                                                syncToParent(true);
                                                
                                                if (onAnalysisComplete) {
                                                    onAnalysisComplete(analysisData);
                                                }
                                                
                                                showSnackbar('D4 Root Cause Analysis completed successfully!', 'success');
                                            } catch (error) {
                                                console.error('Error saving analysis:', error);
                                                showSnackbar('Error completing analysis: ' + error.message, 'error');
                                            }
                                        } else {
                                            syncToParent(false);
                                        }
                                    }}
                                    disabled={isReadOnly || !isFormValid()}
                                    sx={{ 
                                        '& .MuiSvgIcon-root': { 
                                            fontSize: 28, 
                                            color: isFormValid() ? theme.palette.success.main : theme.palette.grey[400] 
                                        } 
                                    }}
                                />
                            }
                            label=""
                        />
                        <Typography variant="h6" sx={{ 
                            fontWeight: 500, 
                            color: isFormValid() ? theme.palette.success.main : theme.palette.grey[600] 
                        }}>
                            D4 Root Cause Analysis Complete - Ready for Corrective Actions
                        </Typography>
                        {!isFormValid() && (
                            <Typography variant="body2" sx={{ color: theme.palette.error.main, fontStyle: 'italic' }}>
                                (Complete all analysis methodologies to enable)
                            </Typography>
                        )}
                    </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </LocalizationProvider>
    );
} 